/**
 * PB-FOUNDATION Topic Multi field orchestration — extracted from pb-logic-core.liquid (PB-FND-33).
 */

import { validateTopicMultiGate } from '@theme/pb-field-runtime';

/**
 * @param {object} deps
 * @param {object} deps.pb — productBuilder instance
 */
export function createTopicMultiHelpers(deps) {
  const pb = deps.pb || deps;

  function extractTopicList(raw) {
    const str = String(raw || '');
    return str.split(',').map((t) => t.trim()).filter((t) => t && !t.includes(':'));
  }

  function getTopicAnswerSettings() {
    return 'required:true, max:500';
  }

  function getTopicPrompt(topicLabel) {
    return `Tell me about ${String(topicLabel || '').trim()}`;
  }

  function parseTopicSettings(raw) {
    const lines = String(raw || '').split('\n');
    const rules = {};
    const topics = [];
    let inTopics = false;
    lines.forEach((line) => {
      const trimmed = String(line || '').trim();
      if (!inTopics) {
        if (trimmed === '') {
          inTopics = true;
          return;
        }
        const match = trimmed.match(/^([a-zA-Z0-9_]+)\s*:\s*(.+)$/);
        if (match) {
          rules[match[1].toLowerCase()] = match[2].trim();
        }
      } else if (trimmed !== '') {
        topics.push(trimmed);
      }
    });
    if (!inTopics) {
      return { rules: {}, topics: lines.map((l) => String(l || '').trim()).filter(Boolean) };
    }
    return { rules, topics };
  }

  function applyTopicRules(grid, rules) {
    if (!grid) return;
    const min = parseInt(rules.min || 0);
    const max = parseInt(rules.max || 0);
    const required = min > 0 ? 'true' : String(rules.required || '').toLowerCase() === 'true' ? 'true' : 'false';
    const parts = [];
    if (required === 'true') parts.push('required:true');
    if (min > 0) parts.push(`min:${min}`);
    if (max > 0) parts.push(`max:${max}`);
    grid.dataset.settings = parts.join(', ');
    grid.dataset.topicRules = JSON.stringify(rules);
    pb.validateButtonGrid(grid);
  }

  function renderTopicButtons(grid, topics) {
    if (!grid) return;
    const ctx = pb.getActiveContext();
    const stepEl = grid.closest('.pb-step');
    const fieldLabel = stepEl?.dataset?.topicLabel || '';
    const selected = Array.isArray(ctx.topicSelections?.[fieldLabel]) ? ctx.topicSelections[fieldLabel] : [];
    const html = (topics || [])
      .map((topic) => {
        const template = String(topic || '').trim();
        if (!template) return '';
        const resolved = pb.resolvePersonalizationTokens(template, ctx);
        return `<label class="pb-topic-row"><input type="checkbox" class="pb-topic-checkbox" data-value="${pb.escapeAttr(resolved)}" data-pb-template="${pb.escapeAttr(template)}" data-pb-value-target="self" onclick="window.productBuilder.toggleTopic(this)"> <span class="pb-topic-label" data-pb-template="${pb.escapeAttr(template)}">${pb.escapeAttr(resolved)}</span></label>`;
      })
      .join('');
    grid.innerHTML = html;
    grid.dataset.topicRendered = 'true';
    grid.querySelectorAll('.pb-topic-checkbox').forEach((cb) => {
      const template = cb.dataset.pbTemplate || '';
      if (template && selected.includes(template)) cb.checked = true;
    });
  }

  function getTopicAnswerSettingsFromRules(rules) {
    const required = String(rules.answer_required || 'true').toLowerCase() !== 'false';
    const min = parseInt(rules.answer_min || 0);
    const max = parseInt(rules.answer_max || 500);
    const placeholder = rules.answer_placeholder || '';
    const recorderOn = ['true', 'on'].includes(String(rules.answer_recorder || '').toLowerCase());
    const recorderMax = parseInt(rules.answer_recorder_max || 300) || 300;
    const parts = [];
    if (required) parts.push('required:true');
    if (min > 0) parts.push(`min:${min}`);
    if (max > 0) parts.push(`max:${max}`);
    if (placeholder) parts.push(`placeholder:${placeholder}`);
    if (recorderOn) parts.push('recorder:true');
    if (recorderOn) parts.push(`recorder_max:${recorderMax}`);
    return parts.join(', ');
  }

  function audioPropertyLabel(textLabel) {
    return String(textLabel || '').trim() + ' (audio)';
  }

  function topicHasSavedAnswer(ctx, topicTemplate) {
    if (!ctx || !topicTemplate) return false;
    const resolved = pb.resolvePersonalizationTokens(topicTemplate, ctx);
    if (!resolved) return false;
    const text = ctx.selections?.[resolved];
    const audio = ctx.selections?.[audioPropertyLabel(resolved)];
    const hasText = text != null && String(text).trim() !== '';
    const hasAudio = audio != null && String(audio).trim() !== '';
    return hasText || hasAudio;
  }

  function inferTopicSelectionsFromAnswers(ctx, fields) {
    if (!ctx || !fields?.length) return;
    ctx.topicSelections = ctx.topicSelections || {};
    fields.forEach((field) => {
      if (!field?.type || !String(field.type).includes('TopicMulti') || !field.label) return;
      const fieldLabel = field.label;
      const settingsStr = pb.settingsToString(field.settings);
      const parsed = parseTopicSettings(settingsStr);
      const inferred = parsed.topics.filter((t) => topicHasSavedAnswer(ctx, t));
      if (!inferred.length) return;
      const existing = Array.isArray(ctx.topicSelections[fieldLabel]) ? ctx.topicSelections[fieldLabel] : [];
      const merged = existing.slice();
      inferred.forEach((t) => {
        if (merged.indexOf(t) === -1) merged.push(t);
      });
      ctx.topicSelections[fieldLabel] = merged;
    });
  }

  function restoreTopicMultiFromSavedAnswers(containerSelector, fields) {
    const root = typeof containerSelector === 'string' ? document.querySelector(containerSelector) : containerSelector;
    if (!root) return;
    const ctx = pb.getActiveContext();
    const fieldList = fields || [];
    if (fieldList.length) inferTopicSelectionsFromAnswers(ctx, fieldList);
    const topicSteps = Array.from(root.querySelectorAll('.pb-step[data-topic-multi="true"]')).sort((a, b) => {
      const ai = pb.getStepIndexFromId(a.id) || 0;
      const bi = pb.getStepIndexFromId(b.id) || 0;
      return ai - bi;
    });
    topicSteps.forEach((stepEl) => {
      pb.buildTopicSteps(stepEl);
    });
    if (typeof pb.assertStepViewportsAligned === 'function') {
      pb.assertStepViewportsAligned(containerSelector);
    }
    const contentSteps = root.querySelectorAll('.pb-bottom-wrapper .pb-step-viewport .pb-step');
    if (contentSteps.length) ctx.maxSteps = contentSteps.length;
    if (containerSelector === '#main-flow-container' && typeof pb.syncMainMaxStepsFromDom === 'function') {
      pb.syncMainMaxStepsFromDom();
    }
  }

  function getPairedImageStepForControlStep(stepEl, containerID, prefix) {
    const root = typeof containerID === 'string' ? document.querySelector(containerID) : containerID;
    if (!root || !stepEl) return null;
    const imageViewport = root.querySelector('.pb-modal-body .pb-step-viewport');
    if (!imageViewport) return null;
    const stepIndex = pb.getStepIndexFromId(stepEl.id);
    if (stepIndex != null) {
      return imageViewport.querySelector(`#${prefix}img-page-${stepIndex}`);
    }
    const controlViewport = root.querySelector('.pb-bottom-wrapper .pb-step-viewport');
    if (!controlViewport) return null;
    const contentSteps = controlViewport.querySelectorAll('.pb-step');
    let idx = -1;
    contentSteps.forEach((el, i) => {
      if (el === stepEl) idx = i;
    });
    if (idx < 0) return null;
    const imgSteps = imageViewport.querySelectorAll('.pb-step');
    return imgSteps[idx] || null;
  }

  function assertStepViewportsAligned(containerSelector) {
    const root = typeof containerSelector === 'string' ? document.querySelector(containerSelector) : containerSelector;
    if (!root) return true;
    const imgCount = root.querySelectorAll('.pb-modal-body .pb-step-viewport .pb-step').length;
    const controlCount = root.querySelectorAll('.pb-bottom-wrapper .pb-step-viewport .pb-step').length;
    if (imgCount === controlCount) return true;
    console.warn('[PB] Step viewport mismatch — image:', imgCount, 'control:', controlCount);
    return false;
  }

  function flushTopicMultiStep(stepEl, ctx) {
    if (!stepEl || !ctx) return;
    const fieldLabel = stepEl.dataset?.topicLabel || '';
    if (stepEl.dataset?.topicMulti === 'true' && fieldLabel) {
      const grid = stepEl.querySelector('[data-topic-list]');
      ctx.topicSelections = ctx.topicSelections || {};
      if (grid) {
        const selected = [];
        grid.querySelectorAll('.pb-topic-checkbox:checked').forEach((cb) => {
          const template = cb.dataset.pbTemplate || cb.dataset.value || '';
          if (template) selected.push(template);
        });
        // Never clobber saved topic picks with an empty DOM read (off-screen / unmounted steps).
        if (selected.length > 0) {
          ctx.topicSelections[fieldLabel] = selected;
        }
      }
    }
    const escLabel = pb.escapeSelector(fieldLabel);
    const containerID = window.builderState?.mode === 'MAIN' ? '#main-flow-container' : '#sub-flow-container';
    const root = document.querySelector(containerID);
    if (!root || !fieldLabel) return;
    root.querySelectorAll(`.pb-step[data-topic-parent="${escLabel}"]`).forEach((answerStep) => {
      const topic = answerStep.dataset.topicValue || '';
      if (!topic) return;
      const textarea = answerStep.querySelector('textarea[data-label], .pb-long-text, .pb-topic-answer');
      if (textarea) {
        ctx.selections[topic] = String(textarea.value || '').trim();
      }
      const audioLabel = audioPropertyLabel(topic);
      const audioHidden = answerStep.querySelector(`input[data-label="${pb.escapeSelector(audioLabel)}"]`);
      if (audioHidden && audioHidden.value) {
        ctx.selections[audioLabel] = audioHidden.value;
      }
    });
  }

  function validateTopicMultiStep(stepEl) {
    return validateTopicMultiGate(stepEl);
  }

  function flushTopicAnswersFromDom(containerSelector) {
    const sel = containerSelector || (window.builderState?.mode === 'SUB' ? '#sub-flow-container' : '#main-flow-container');
    const root = document.querySelector(sel);
    if (!root) return;
    const ctx = pb.getActiveContext();
    root.querySelectorAll('.pb-step[data-topic-multi="true"]').forEach((stepEl) => {
      flushTopicMultiStep(stepEl, ctx);
    });
  }

  function toggleTopic(el) {
    if (!el) return;
    const ctx = pb.getActiveContext();
    const stepEl = el.closest('.pb-step');
    const fieldLabel = stepEl?.dataset?.topicLabel || '';
    const topic = el.dataset.value || el.value || '';
    if (!fieldLabel || !topic) return;
    ctx.topicSelections = ctx.topicSelections || {};
    const list = Array.isArray(ctx.topicSelections[fieldLabel]) ? ctx.topicSelections[fieldLabel] : [];
    const grid = el.closest('.pb-topic-multi') || el.closest('.btn-grid');
    const rules = grid?.dataset?.topicRules ? JSON.parse(grid.dataset.topicRules) : {};
    const max = parseInt(rules.max || 0);
    const topicTemplate = el.dataset.pbTemplate || topic;
    const idx = list.indexOf(topicTemplate);
    if (idx >= 0) {
      list.splice(idx, 1);
      if (el.classList.contains('pb-topic-checkbox')) el.checked = false;
      else el.classList.remove('active');
    } else {
      if (max > 0 && list.length >= max) {
        if (el.classList.contains('pb-topic-checkbox')) el.checked = false;
        return;
      }
      list.push(topicTemplate);
      if (el.classList.contains('pb-topic-checkbox')) el.checked = true;
      else el.classList.add('active');
    }
    ctx.topicSelections[fieldLabel] = list;
    if (grid) pb.checkButtonValidity(grid);
  }

  function buildTopicSteps(stepEl) {
    if (pb._pbBuildingTopicSteps) return;
    pb._pbBuildingTopicSteps = true;
    try {
      return buildTopicStepsInner(stepEl);
    } finally {
      pb._pbBuildingTopicSteps = false;
    }
  }

  function buildTopicStepsInner(stepEl) {
    const s = window.builderState;
    const ctx = pb.getActiveContext();
    const containerID = s.mode === 'MAIN' ? '#main-flow-container' : '#sub-flow-container';
    const prefix = s.mode === 'MAIN' ? '' : 'sub-';
    const root = document.querySelector(containerID);
    if (!root || !stepEl) return;
    const fieldLabel = stepEl.dataset.topicLabel || '';
    const grid = stepEl.querySelector('[data-topic-list]');
    if (!grid || !fieldLabel) return;
    const parsed = parseTopicSettings(grid.dataset.topicList || '');
    const selected = Array.isArray(ctx.topicSelections?.[fieldLabel]) ? ctx.topicSelections[fieldLabel] : [];
    const ordered = parsed.topics.filter((t) => selected.includes(t));
    const controlViewport = root.querySelector('.pb-bottom-wrapper .pb-step-viewport');
    const imageViewport = root.querySelector('.pb-modal-body .pb-step-viewport');
    if (!controlViewport || !imageViewport) return;

    const escLabel = pb.escapeSelector(fieldLabel);
    const prevControlSteps = Array.from(controlViewport.querySelectorAll(`.pb-step[data-topic-parent="${escLabel}"]`));
    prevControlSteps.forEach((el) => {
      const resolvedTopic = el.dataset.topicValue || '';
      const stillSelected = ordered.some((t) => pb.resolvePersonalizationTokens(t, ctx) === resolvedTopic);
      if (resolvedTopic && !stillSelected) {
        delete ctx.selections[resolvedTopic];
        delete ctx.selections[audioPropertyLabel(resolvedTopic)];
      }
      window.pbAudioEngine?.disposeFieldScope(el);
      el.remove();
    });
    imageViewport.querySelectorAll(`.pb-step[data-topic-parent="${escLabel}"]`).forEach((el) => el.remove());

    if (ordered.length === 0) {
      pb.renumberSteps(containerID, prefix);
      return;
    }

    let insertAfterContent = stepEl;
    let insertAfterImage = getPairedImageStepForControlStep(stepEl, containerID, prefix);
    const settingsStr = getTopicAnswerSettingsFromRules(parsed.rules);
    const bgImg =
      s.mode === 'MAIN'
        ? window.BUILDER_DATA?.mainProduct?.images?.[0]?.src || window.BUILDER_DATA?.mainProduct?.image || ''
        : s.sub.activeImages?.[0]?.src ||
          (Array.isArray(s.sub.activeImages) && s.sub.activeImages[0] && typeof s.sub.activeImages[0] === 'object'
            ? s.sub.activeImages[0].src
            : '') ||
          '';
    const topicDesc = parsed.rules?.answer_description ? String(parsed.rules.answer_description).trim() : '';

    ordered.forEach((topic) => {
      const resolvedTopic = pb.resolvePersonalizationTokens(topic, ctx);
      const prompt = getTopicPrompt(resolvedTopic);
      const contentStep = document.createElement('div');
      contentStep.className = 'pb-step pb-step-hide-question';
      contentStep.dataset.topicParent = fieldLabel;
      contentStep.dataset.topicValue = resolvedTopic;
      const existingTopicVal = ctx.selections?.[resolvedTopic] || '';
      const existingTopicAudio = ctx.selections?.[audioPropertyLabel(resolvedTopic)] || '';
      const topicFieldHtml = pb.buildLongTextFieldHtml({
        label: resolvedTopic,
        settings: settingsStr,
        existingVal: existingTopicVal,
        existingAudio: existingTopicAudio,
        extraClass: 'pb-topic-answer',
      });
      contentStep.innerHTML = `<div class="step-content-inner"><span class="option-label"><span class="pb-question-text" data-pb-template="${pb.escapeAttr(topic)}" data-pb-value-target="topic-prompt">${pb.escapeAttr(prompt)}</span></span>${topicFieldHtml}</div>`;
      insertAfterContent.insertAdjacentElement('afterend', contentStep);
      insertAfterContent = contentStep;

      if (insertAfterImage) {
        const imgStep = document.createElement('div');
        imgStep.className = 'pb-step';
        imgStep.dataset.topicParent = fieldLabel;
        imgStep.dataset.topicValue = topic;
        const descHtml = topicDesc
          ? `<div class="pb-question-highlight-desc" data-pb-template="${pb.escapeAttr(topicDesc)}">${pb.escapeAttr(topicDesc)}</div>`
          : '';
        imgStep.innerHTML = `<div class="pb-question-highlight" style="--pb-highlight-bg: url('${pb.escapeAttr(bgImg)}');"><div class="pb-question-highlight-bg"></div><div class="pb-question-highlight-inner"><span class="pb-question-highlight-text pb-preview-display" data-pb-template="${pb.escapeAttr(topic)}" data-pb-value-target="topic-prompt">${pb.escapeAttr(prompt)}</span>${descHtml}</div></div>`;
        insertAfterImage.insertAdjacentElement('afterend', imgStep);
        insertAfterImage = imgStep;
      }
    });

    pb.renumberSteps(containerID, prefix);
    if (typeof window.pbLongTextRecorder?.init === 'function') {
      window.pbLongTextRecorder.init(containerID);
    }
    if (typeof pb.syncPersonalizationTemplates === 'function') {
      pb.syncPersonalizationTemplates(containerID);
    }
    requestAnimationFrame(function () {
      if (typeof pb._syncMobileKeyboardChrome === 'function') pb._syncMobileKeyboardChrome();
    });
  }

  return {
    extractTopicList,
    getTopicAnswerSettings,
    getTopicPrompt,
    parseTopicSettings,
    applyTopicRules,
    renderTopicButtons,
    getTopicAnswerSettingsFromRules,
    audioPropertyLabel,
    topicHasSavedAnswer,
    inferTopicSelectionsFromAnswers,
    restoreTopicMultiFromSavedAnswers,
    getPairedImageStepForControlStep,
    assertStepViewportsAligned,
    flushTopicMultiStep,
    validateTopicMultiStep,
    flushTopicAnswersFromDom,
    toggleTopic,
    buildTopicSteps,
  };
}

const TOPIC_METHODS = [
  'extractTopicList',
  'getTopicAnswerSettings',
  'getTopicPrompt',
  'parseTopicSettings',
  'applyTopicRules',
  'renderTopicButtons',
  'getTopicAnswerSettingsFromRules',
  'audioPropertyLabel',
  'topicHasSavedAnswer',
  'inferTopicSelectionsFromAnswers',
  'restoreTopicMultiFromSavedAnswers',
  'getPairedImageStepForControlStep',
  'assertStepViewportsAligned',
  'flushTopicMultiStep',
  'validateTopicMultiStep',
  'flushTopicAnswersFromDom',
  'toggleTopic',
  'buildTopicSteps',
];

function attachToWindow() {
  if (typeof window === 'undefined') return;
  window.PBTopicMulti = { createTopicMultiHelpers };
}

function install(pb) {
  if (!pb || !window.PBTopicMulti) return false;
  if (pb._pbTopicMultiInstalled) return true;
  const topic = createTopicMultiHelpers({ pb });
  pb._pbTopicMultiInstalled = true;
  pb._pbTopicMulti = topic;
  TOPIC_METHODS.forEach((name) => {
    pb[name] = function (...args) {
      return topic[name](...args);
    };
  });
  return true;
}

function tryInstall() {
  if (typeof window === 'undefined' || !window.productBuilder) return false;
  return install(window.productBuilder);
}

attachToWindow();

if (typeof window !== 'undefined') {
  window.__pbEnsureTopicMulti = install;
  if (!tryInstall()) {
    document.addEventListener('DOMContentLoaded', tryInstall);
    document.addEventListener('builder:ready', tryInstall);
  }
}
